
import { CheckCircleOutlined, PlusOutlined, StopOutlined, UndoOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Skeleton, Space, Tag, Tooltip } from "antd";
import { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { updateAgencyUser } from "../../../assets/api/agency";
import { ReactComponent as EditIcon } from "../../../assets/Icons/Editicon.svg";
import { ReactComponent as SearchICon } from "../../../assets/Icons/SearchIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/Icons/TrashIcon.svg";
import CustomFilterDropdown from "../../../Reuseable/Allocation/CustomFilterDropdown/CustomFilterDropdown";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import ReusableTable from "../../../Reuseable/ReusableTable/ReusableTable";
import { fetchAgencyUsers } from "../../../store/agencySlice";
import AddUserModal from "./AddUserModal";
import DeleteUserModal from "./DeleteUserModal";
import EditUserModal from "./EditUserModal";
import UserInfo from "./UserInfo";
import "./UserList.scss";
import { deleteUser, updateUserStatus } from "../../../api/AddUser";

const UserList = ({
  data,
  loading = false,
  bankData,
  areaCodes,
  onPaginationChange,
  currentPage,
  pageSize,
  setCurrentPage,
  setPageSize,
  isAddUser = true,
}) => {
  const { token } = useSelector((state) => state?.auth);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewUserData, setViewUserData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const [load, setLoad] = useState({});
  const dispatch = useDispatch();



  const handleDeleteClick = async (record) => {
    setUserToDelete(record);
    setIsDeleteModalOpen(true);
    setSelectedUser(record);

  };

  const status = {
    Inactive: 'enable',
    Active: 'disable'
  }

  const handleConfirmDelete = async () => {
    if (!selectedUser?.id || !token) return;
    setDeleteLoading(true);
    try {
      const res = await updateUserStatus(selectedUser.id, status?.[selectedUser?.status], token);
      if (res?.status === 200 || res?.status === 'success') {
        toast.success(res?.message);
        dispatch(fetchAgencyUsers({ page: currentPage, page_size: pageSize }));
        setIsDeleteModalOpen(false);
        setDeleteLoading(false);
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to delete user');
      setDeleteLoading(false);

    }
  };

  const handleViewClick = (record) => {
    setViewUserData(record);
    setIsViewModalOpen(true);
  };

  const handleEditClick = (record) => {
    setSelectedUser(record);
    setIsEditModalOpen(true);
  };

  const handleSaveDetails = async (updatedData) => {
    const payload = {
      id: selectedUser?.id,
      name: updatedData?.name,
      address: updatedData?.address,
      area: updatedData?.area,
      mobile_no: updatedData?.mobile_no,
      mother_tongue: updatedData?.mother_tongue,
      product: updatedData?.product,
      region: updatedData?.region,
      bucket: updatedData?.bucket,
      portfolio: updatedData?.portfolio?.map((item) => item?.value ?? item),
      tele_callers: updatedData?.telleCaller,
      field_agents: updatedData?.fieldAgenst,
      team_leads: updatedData?.teamLeads,
      call_list_id: updatedData?.campaign_id,
      agent_id: updatedData?.agent_id,
    };

    try {
      setLoad((prev) => ({
        ...prev,
        updatedUser: true,
      }));
      const res = await updateAgencyUser(payload, token);

      if (res?.status === 200) {
        toast.success(res?.data?.message);
        dispatch(fetchAgencyUsers({ page: 1, page_size: 10 }));
        setIsEditModalOpen(false);
        setLoad((prev) => ({
          ...prev,
          updatedUser: false,
        }));

        setCurrentPage(1);
        setPageSize(10);
      }
    } catch (error) {
      toast.error(error?.message);
      setIsEditModalOpen(false);
      setLoad((prev) => ({
        ...prev,
        updatedUser: false,
      }));
    }
  };

  const filters = [
    { label: "All", value: "" },
    { label: "Supervisor", value: "supervisor" },
    { label: "Team Lead", value: "teamlead" },
    { label: "Telecaller", value: "telecaller" },
    { label: "Field Agent", value: "fieldagent" },
  ];

  const columns = [

    {
      title: "Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "NA",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "NA",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "NA",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <Tag
            className={`statusIndicator ${status === "Active" ? "activeStatus" : "inactiveStatus"
              }`}
            color={status === "Active" ? "green" : "red"}
          >
            {status}
          </Tag>
        ),
    },
    {
      title: "Details",
      key: "details",
      render: (_, record) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <Button
            type="default"
            danger
            className="viewBtn"
            onClick={() => handleViewClick(record)}
          >
            View Details
          </Button>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <Space>
            <Button
              type="text"
              icon={<EditIcon />}
              onClick={() => handleEditClick(record)}
            />
            <Tooltip title={record?.status === "Inactive" ? "Enable user" : "Disable user"}>
              <Button
                danger={record?.status !== "Inactive"}
                icon={record?.status === "Inactive" ? <UndoOutlined /> : <StopOutlined />}
                onClick={() => handleDeleteClick(record)}
                style={{ height: "27px", width: "26px" }}
              />
            </Tooltip>
          </Space>
        ),
    },
  ];

  return (
    <div className="userListContainer">
      <Flex
        justify="space-between"
        align="center"
        className="topBar"
        // wrap="wrap"
        gap={16}
      >
        <Input
          placeholder="Search"
          allowClear
          className="searchInput"
          prefix={<SearchICon />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={(e) => {
            const value = e.target.value;

            const query = {
              page: 1,
              page_size: pageSize,
              ...(roleFilter ? { role: roleFilter } : {}),
              ...(value ? { search: value } : {}),
            };

            setCurrentPage(1);
            dispatch(fetchAgencyUsers(query));
          }}
        />


        <Space>
          {isAddUser && (
            <NormalButton
              type="primary"
              suffixIcon={<PlusOutlined />}
              onClick={() => setIsAddModalOpen(true)}
            >
              Add User
            </NormalButton>
          )}

          <CustomFilterDropdown
            buttonLabel="Filter"
            options={filters}
            defaultValue="all"
            onChange={(val) => {
              setRoleFilter(val);
              const query = {
                page: 1,
                page_size: pageSize,
                ...(val ? { role: val } : {}),
                ...(searchText ? { search: searchText } : {}),
              };
              dispatch(fetchAgencyUsers(query));
              setCurrentPage(1);
            }}
          />
        </Space>
      </Flex>
      <ReusableTable
        columns={columns}
        dataSource={
          loading
            ? Array(10)
              .fill(0)
              .map((_, i) => ({ id: `skeleton-${i}` }))
            : data?.results
        }
        totalItems={data?.count}

        CurrentPageNo={currentPage}
        currentPage={currentPage}
        pageSize={pageSize}
        triggerChange={(page, size) => {
          onPaginationChange(page, size);
          setCurrentPage(page);
          setPageSize(size);

          // const query = {
          //   page,
          //   page_size: size,
          //   ...(searchText ? { search: searchText } : {}),
          //   ...(roleFilter ? { role: roleFilter } : {}),
          // };

          // dispatch(fetchAgencyUsers(query));
        }}
      />

      <EditUserModal
        areaCodes={areaCodes}
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        userData={selectedUser}
        setSelectedUser={setSelectedUser}
        onSave={handleSaveDetails}
        data={data?.results}
        loading={load?.updatedUser}
        bankData={bankData}
      />

      <UserInfo
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        userDetails={viewUserData}
      />
      <DeleteUserModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleConfirmDelete}
        loading={deleteLoading}
        userStatus={selectedUser?.status}
      />

      <AddUserModal
        bankData={bankData}
        areaCodes={areaCodes}
        loading={loading}
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={(data) => {
          dispatch(fetchAgencyUsers());
          setIsAddModalOpen(false);
        }}
      />
    </div>
  );
};

export default UserList;
